<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { createContactSearcher } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const emit = defineEmits(['added']);

const { t } = useI18n();
const dialogRef = ref(null);
const isAdding = ref(false);

const participants = ref([]);
const contactResults = ref([]);
const isSearchingContacts = ref(false);
const showContactsDropdown = ref(false);

const searchContacts = createContactSearcher();

const isFormInvalid = computed(() => !participants.value.length);

const contactMenuItems = computed(() =>
  contactResults.value
    .filter(contact => contact.phoneNumber)
    .map(contact => ({
      value: contact.phoneNumber,
      label: `${contact.name} (${contact.phoneNumber})`,
      phoneNumber: contact.phoneNumber,
      action: 'contact',
    }))
);

const onParticipantSearch = debounce(
  async query => {
    if (!query) {
      contactResults.value = [];
      showContactsDropdown.value = false;
      return;
    }
    showContactsDropdown.value = true;
    isSearchingContacts.value = true;
    try {
      const results = await searchContacts(query, { reachableOnly: false });
      contactResults.value = results || [];
    } finally {
      isSearchingContacts.value = false;
    }
  },
  400,
  false
);

const resetForm = () => {
  participants.value = [];
  contactResults.value = [];
  showContactsDropdown.value = false;
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  if (isFormInvalid.value || isAdding.value) return;
  isAdding.value = true;
  try {
    await ConversationAPI.addEvolutionGroupParticipants(
      props.conversationId,
      participants.value
    );
    useAlert(t('CONTACT_PANEL.ADD_EVOLUTION_GROUP_PARTICIPANTS.SUCCESS'));
    emit('added');
    closeDialog();
  } catch (error) {
    useAlert(t('CONTACT_PANEL.ADD_EVOLUTION_GROUP_PARTICIPANTS.ERROR'));
  } finally {
    isAdding.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACT_PANEL.ADD_EVOLUTION_GROUP_PARTICIPANTS.TITLE') }}
      </span>
      <div
        class="px-2 py-2 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong"
      >
        <TagInput
          v-model="participants"
          type="tel"
          allow-create
          :menu-items="contactMenuItems"
          :show-dropdown="showContactsDropdown"
          :is-loading="isSearchingContacts"
          :disabled="isAdding"
          :placeholder="
            t('CONTACT_PANEL.ADD_EVOLUTION_GROUP_PARTICIPANTS.PLACEHOLDER')
          "
          @input="onParticipantSearch"
          @on-click-outside="showContactsDropdown = false"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('CONTACT_PANEL.ADD_EVOLUTION_GROUP_PARTICIPANTS.HELP_TEXT') }}
      </p>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="t('CONTACT_PANEL.ADD_EVOLUTION_GROUP_PARTICIPANTS.SUBMIT')"
          color="blue"
          type="submit"
          :disabled="isFormInvalid || isAdding"
          :is-loading="isAdding"
        />
      </div>
    </template>
  </Dialog>
</template>
